import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import DashboardLayout from "../../components/layout/DashboardLayout";
import SimpleTable from "../../components/common/SimpleTable";
import BackToDashboard from "../../components/common/BackToDashboard";
import { setPageTitle } from "../../utils/document";
import { getMasterDataLinks } from "../../api/masterDataLinks";

const ExternalLinkIcon = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
    stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9" />
    <polyline points="10 1 15 1 15 6" />
    <line x1="15" y1="1" x2="7" y2="9" />
  </svg>
);

const LockIcon = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
    stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="7" width="10" height="8" rx="1" />
    <path d="M5 7V5a3 3 0 0 1 6 0v2" />
  </svg>
);

// ── Cell renderers ─────────────────────────────────────────────────────────────

const OpenButton = ({ url, t }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display:        "inline-flex",
      alignItems:     "center",
      gap:            5,
      padding:        "4px 10px",
      borderRadius:   6,
      background:     t.accent2,
      border:         `1px solid ${t.accent}44`,
      textDecoration: "none",
      cursor:         "pointer",
      transition:     "opacity .15s",
      whiteSpace:     "nowrap",
    }}
    onMouseEnter={e => (e.currentTarget.style.opacity = "0.7")}
    onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
  >
    <ExternalLinkIcon color={t.accent} />
    <span style={{
      fontSize: 11, fontWeight: 600, letterSpacing: ".05em",
      textTransform: "uppercase", color: t.accent,
    }}>
      Open
    </span>
  </a>
);

const PendingButton = ({ t }) => (
  <span style={{
    display:      "inline-flex",
    alignItems:   "center",
    gap:          5,
    padding:      "4px 10px",
    borderRadius: 6,
    background:   t.surface2,
    border:       `1px solid ${t.border2}`,
    cursor:       "not-allowed",
    whiteSpace:   "nowrap",
  }}>
    <LockIcon color={t.text3} />
    <span style={{
      fontSize: 11, fontWeight: 600, letterSpacing: ".05em",
      textTransform: "uppercase", color: t.text3,
    }}>
      Link Pending
    </span>
  </span>
);

// ── Column definitions ─────────────────────────────────────────────────────────

const COLUMNS = [
  {
    key:    "name",
    label:  "Data Asset",
    render: (row, _isDark, t) => (
      <span style={{ fontWeight: 600, fontSize: 12, color: t.text }}>
        {row.name}
      </span>
    ),
  },
  {
    key:    "owningFunction",
    label:  "Owning Function",
    render: (row, _isDark, t) => (
      <span style={{
        fontSize: 11, fontFamily: "monospace",
        color: t.text3, letterSpacing: ".04em",
      }}>
        {row.owningFunction}
      </span>
    ),
  },
  {
    key:    "owner",
    label:  "Owner",
    render: (row, _isDark, t) => (
      <span style={{ fontSize: 12, color: t.text2 }}>
        {row.ownerName}
      </span>
    ),
  },
  {
    key:    "action",
    label:  "",
    align:  "right",
    render: (row, _isDark, t) =>
      row.url
        ? <OpenButton url={row.url} t={t} />
        : <PendingButton t={t} />,
  },
];

// ── Page ──────────────────────────────────────────────────────────────────────

const ResourcesPage = () => {
  const theme = useTheme();
  const t     = theme.custom;

  // Sourced from the MasterDataLinks table rather than a constant, so a moved
  // SharePoint list or a changed owner can be corrected without a release.
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => { setPageTitle("Master Datasets"); }, []);

  useEffect(() => {
    let cancelled = false;

    getMasterDataLinks()
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((err) => {
        console.error("Failed to load master data links:", err);
        // Surfaced rather than swallowed — an empty table would otherwise
        // read as "there are no datasets", which is a different thing.
        if (!cancelled) setError("Could not load master datasets. Please try again.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return (
    <DashboardLayout>
      <div style={{
        display:       "flex",
        flexDirection: "column",
        height:        "100%",
        overflowY:     "auto",
        padding:       24,
        boxSizing:     "border-box",
        background:    t.bg,
        gap:           16,
      }}>

        {/* Back + page title */}
        <div>
          <div style={{ marginBottom: 10 }}>
            <BackToDashboard />
          </div>
          <div style={{
            fontSize:      15,
            fontWeight:    600,
            color:         t.text,
            letterSpacing: "-0.01em",
          }}>
            Master Datasets
          </div>
        </div>

        {/* Table */}
        {error ? (
          <div style={{ fontSize: 12, color: "#c84040" }}>{error}</div>
        ) : loading ? (
          <div style={{ fontSize: 12, color: t.text3 }}>Loading master datasets...</div>
        ) : (
          <SimpleTable
            title="Master Data Sources"
            rows={rows}
            columns={COLUMNS}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ResourcesPage;
